/**
 * Owl session – soft email auth.
 *
 * Storage lives in localStorage so the user stays signed in across tabs
 * + page reloads. Two keys:
 *   owl.token  → opaque session token issued by POST /identify
 *   owl.email  → the email the user typed (display only)
 *
 * No cookies, no Authorization header – we send the token as
 * `X-Owl-Session` on every request. Avoids the cross-origin cookie
 * dance and works dev (http://localhost:5173 → http://localhost:8000)
 * and prod (https://owl.nyvo.money → https://api.nyvo.money) the same.
 *
 * If the token is rejected (e.g. user signed out elsewhere), we clear
 * local state silently and fall back to anonymous mode. Owl is meant
 * to work without an account.
 */

const TOKEN_KEY = "owl.token";
const EMAIL_KEY = "owl.email";

export interface OwlSession {
  email: string;
  token: string;
}

function safeStorage(): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    // Touch storage to verify it's writable (Safari private mode disables it).
    window.localStorage.setItem("__owl_probe__", "1");
    window.localStorage.removeItem("__owl_probe__");
    return window.localStorage;
  } catch {
    return null;
  }
}

export function readSession(): OwlSession | null {
  const s = safeStorage();
  if (!s) return null;
  const token = s.getItem(TOKEN_KEY);
  const email = s.getItem(EMAIL_KEY);
  if (!token || !email) return null;
  return { token, email };
}

export function writeSession(sess: OwlSession): void {
  const s = safeStorage();
  if (!s) return;
  s.setItem(TOKEN_KEY, sess.token);
  s.setItem(EMAIL_KEY, sess.email);
}

export function clearSession(): void {
  const s = safeStorage();
  if (!s) return;
  s.removeItem(TOKEN_KEY);
  s.removeItem(EMAIL_KEY);
}

/**
 * One-time first-visit marker. The IdentifyModal opens for first-time
 * visitors; we set this flag the first time the user either signs in
 * or skips so we don't badger them on every reload.
 */
const PROMPT_KEY = "owl.identifyPrompted";

export function hasBeenPrompted(): boolean {
  const s = safeStorage();
  return s?.getItem(PROMPT_KEY) === "1";
}

export function markPrompted(): void {
  const s = safeStorage();
  s?.setItem(PROMPT_KEY, "1");
}
